import { useQuery } from '@tanstack/react-query'
import { hubsFor } from '@kronos/domain'
import type { Role } from '@kronos/domain'
import { repository } from '../../data'
import type { BoardOrder } from '../../data'
import type { WorkRow } from '../../data/repository'
import { BuildProgress } from '../BuildProgress'
import { formatDay } from '../dates'
import { EmptyState, Panel, Section } from '../primitives'
import { Link } from '../shell/Link'

/**
 * What is in front of you: your own open jobs first, then the build orders on
 * the floor, then everyone else's open work. This is what used to be the Work
 * tab, moved to where people land — "what do I do now" should be answered
 * before anybody has to go looking.
 */
export function YourWork({ meId, roles }: { meId: string; roles: readonly Role[] }): JSX.Element {
  const work = useQuery({ queryKey: ['work', false], queryFn: () => repository.work(false) })
  const seesBuild = hubsFor(roles).includes('build')
  const orders = useQuery({ queryKey: ['build-orders'], queryFn: () => repository.buildOrders(), enabled: seesBuild })

  const rows = work.data ?? []
  const mine = rows.filter((r) => r.performedBy === meId)
  const others = rows.filter((r) => r.performedBy !== meId)
  const live = (orders.data ?? []).filter((o) => o.state === 'released')

  if (work.isPending) return <p className="muted">Loading…</p>
  const nothing = mine.length === 0 && live.length === 0

  return (
    <>
      <Section label="Your work">
        <Panel>
          {nothing ? (
            <EmptyState icon="work" title="Nothing open for you">
              Scan a part to start work on it{seesBuild ? ', or pick up a build under Build' : ''}.
            </EmptyState>
          ) : null}
          {mine.map((r, i) => (
            <WorkItem key={r.id} row={r} lead={i === 0} />
          ))}
          {live.map((o) => (
            <OrderItem key={o.id} order={o} />
          ))}
        </Panel>
      </Section>

      {others.length > 0 ? (
        <Section label="Also open">
          <Panel>
            {others.map((r) => (
              <WorkItem key={r.id} row={r} lead={false} />
            ))}
          </Panel>
        </Section>
      ) : null}
    </>
  )
}

function WorkItem({ row, lead }: { row: WorkRow; lead: boolean }): JSX.Element {
  return (
    <Link to={{ name: 'execution', id: row.id }} className={`work-item${lead ? ' work-item--lead' : ''}`}>
      <span className="stack" style={{ gap: 2, minWidth: 0 }}>
        <span className="row-item__title">{row.procedureName}</span>
        <span className="row-item__meta">
          {row.fleetNumber ?? row.typeName} · <span className="serial">{row.serial}</span>
        </span>
      </span>
      <span className="work-item__progress">
        {row.answered} of {row.steps}
        <span aria-hidden="true"> →</span>
      </span>
    </Link>
  )
}

/** A build order on the floor, with its progress. */
function OrderItem({ order: o }: { order: BoardOrder }): JSX.Element {
  return (
    <Link to={{ name: 'build', section: 'orders' }} className="work-item work-item--order">
      <span className="work-item__title">
        <span className="row-item__title">
          {o.quantity} × {o.typeName} {o.revisionName}
        </span>
        <span className="row-item__meta">due {formatDay(o.needBy)}</span>
      </span>
      <BuildProgress order={o} />
    </Link>
  )
}
